import * as MOCK from './mock';

export const IS_MOCK = import.meta.env.VITE_MOCK === 'true';

// ── Build auth payload ──────────────────────────────────────────────────────
// Every POST to the owner panel must include { secret, session_token?, ...extra }
export function buildPayload(extra: Record<string, unknown> = {}): Record<string, unknown> {
  const secret = sessionStorage.getItem('op_secret') || '';
  const session = sessionStorage.getItem('op_session');
  return {
    secret,
    ...(session ? { session_token: session } : {}),
    ...extra,
  };
}

// ── Mock handler ────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function mockRequest(path: string, body: Record<string, unknown>): Promise<any> {
  await new Promise(r => setTimeout(r, 60));

  if (path === '/panel/data') {
    return {
      session_token: 'mock-session-dev',
      system_cards: MOCK.MOCK_SYSTEM_CARDS,
      guilds: [{ id: '123456789', name: 'SC Labs', member_count: 420 }],
      selected_guild: { id: '123456789', name: 'SC Labs', member_count: 420 },
      uptime: '2d 14h 32m',
    };
  }

  if (path === '/panel/stats') {
    return { system_cards: MOCK.MOCK_SYSTEM_CARDS };
  }

  if (path === '/panel/access') {
    const action = body.action as string | undefined;
    if (action === 'add' || action === 'delete') {
      return { ok: true, message: `Simulated ${action} for ${body.ip}`, access_config: MOCK.MOCK_ACCESS_CONFIG };
    }
    return { access_config: MOCK.MOCK_ACCESS_CONFIG };
  }

  if (path === '/panel/lavalink') {
    return MOCK.MOCK_LAVALINK;
  }

  if (path === '/panel/backroom/fivem-config') {
    const action = body.action as string;
    if (action === 'set') return { ok: true, message: 'FiveM settings saved (mock).' };
    return MOCK.MOCK_FIVEM_CONFIG;
  }

  if (path === '/panel/backroom/fivem-users') {
    const action = body.action as string;
    if (action === 'create') return { ok: true, message: 'User created (mock).', temp_password: 'MOCK-TMP-PW' };
    if (action === 'delete') return { ok: true, message: 'User deleted (mock).' };
    return { users: MOCK.MOCK_FIVEM_USERS };
  }

  if (path === '/panel/apikeys') {
    const action = body.action as string;
    if (action === 'list') return { ok: true, api_keys: [{ identifier: 'mock_script_1', key: 'sclabs_mock123' }] };
    if (action === 'generate') return { ok: true, api_keys: [{ identifier: 'mock_script_1', key: 'sclabs_mock123' }, { identifier: body.identifier as string, key: 'sclabs_new_mock_key' }] };
    if (action === 'revoke') return { ok: true, api_keys: [] };
    return { ok: false, error: 'invalid mock action' };
  }

  if (path === '/panel/backroom/logs') {
    const offset = (body.offset as number) || 0;
    const lines = offset === 0 ? MOCK.MOCK_LOG_LINES : [];
    return { lines, next_offset: MOCK.MOCK_LOG_LINES.join('\n').length };
  }

  if (path === '/panel/backroom/cmd') {
    const cmd = body.cmd as string;
    return { ok: true, message: `[mock] Executed: ${cmd}` };
  }

  if (path === '/panel/control') {
    return { ok: true, message: `[mock] Control: ${body.action}` };
  }

  console.warn('[mock] Unhandled path:', path);
  return {};
}

// ── Real request ────────────────────────────────────────────────────────────
async function realRequest<T = unknown>(path: string, body: Record<string, unknown>): Promise<T> {
  const resp = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    let msg = `HTTP ${resp.status}: ${resp.statusText}`;
    try { const t = await resp.text(); if (t) msg = t; } catch { /* ignore */ }
    const err = new Error(msg) as Error & { status: number };
    err.status = resp.status;
    throw err;
  }

  const ct = resp.headers.get('content-type') || '';
  if (ct.includes('application/json')) return resp.json() as Promise<T>;
  return resp.text() as unknown as T;
}

// ── Unified entry ───────────────────────────────────────────────────────────
export async function post<T = unknown>(
  path: string,
  extra: Record<string, unknown> = {}
): Promise<T> {
  const body = buildPayload(extra);
  if (IS_MOCK) return mockRequest(path, body) as Promise<T>;
  return realRequest<T>(path, body);
}
